// src/components/outputs/pieces/ReadyToPublish.jsx — the «Pronto da pubblicare» block.
//
// The emphasised block at the top of every history card: the primary piece
// (what goes in the destination's main box) with its label and destination,
// its value (PieceValue — the same drawing PieceRow uses), the counter and the
// ONE prominent button «Copia tutto». The button's id is "all-<entryId>" so
// the card's «Copiato» state (copiedId) and the orchestrator's leave-guard
// (an entry copied whole counts as saved) both key on it.
//
// `text` is the primary piece's paste-ready string — primaryText(pieces) of
// src/publish/downloads.js — and the counter counts that exact string (code
// points), not pieceText(piece): the two differ when the entry has a 'sources'
// piece and the "[N]" markers are kept. When `text` is missing the block falls
// back to pieceText(piece).
// `copyText` / `copyHtml` are what the button puts on the clipboard: the card
// passes allPiecesText / allPiecesHtml (every piece, labelled, in one go — the
// same texts the per-piece «Copia» buttons copy one at a time). Without them
// the button copies `text` and, for a 'rich' piece, pieceHtml(piece) as html.
// The button is a one-shot action, not a toggle: no aria-pressed; the label
// change («Copia tutto» → «Copiato») is announced through aria-live.
// A hand selection inside the value box copies clean (cleanCopy.js ::
// onCopyWithinBox on the inner box div): allowlisted tags, no styles, no box.
//
//   ReadyToPublish({ piece, entryId, copiedId, onCopy, t, w, color, text, copyText, copyHtml })

import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import { COLORS, RADII, SHADOW } from '../../../constants/theme.js';
import { pieceText } from '../../../publish/pieces/index.js';
import { SectionLabel } from '../OutputPrimitives.jsx';
import PieceCounter from './PieceCounter.jsx';
import { PieceValue, copyHtmlOf } from './PieceRow.jsx';
import { onCopyWithinBox } from './cleanCopy.js';

const defaultW = (o) => `rgba(255,255,255,${o})`;

/** The id of the ready block's copy button for an entry. */
export const readyCopyId = (entryId) => `all-${entryId}`;

const ReadyToPublish = ({ piece, entryId, copiedId, onCopy, t = {}, w = defaultW, color, text, copyText, copyHtml }) => {
  const { t: tr } = useTranslation('outputs');
  const boxRef = useRef(null);
  if (!piece) return null;
  const accent = color || COLORS.accent;
  const id = readyCopyId(entryId);
  const payload = typeof text === 'string' ? text : pieceText(piece);
  const clipText = typeof copyText === 'string' ? copyText : payload;
  const clipHtml = copyHtml !== undefined ? (copyHtml || null) : copyHtmlOf(piece);
  const active = copiedId === id;

  return (
    <div
      data-testid="ready-to-publish"
      style={{
        marginBottom: '18px',
        padding: '14px 16px',
        borderRadius: RADII.control,
        background: `${accent}08`,
        border: `1px solid ${accent}33`,
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
          marginBottom: '10px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <SectionLabel color={accent} t={t}>{tr('pieces.ui.ready')}</SectionLabel>
          <div style={{ fontSize: '13px', fontWeight: 600, color: t.text }}>
            {piece.label}
            {piece.destination ? (
              <span style={{ fontWeight: 400, color: t.textMuted || COLORS.textTertiary }}>
                {' · '}{piece.destination}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          id={id}
          data-testid="copy-primary"
          aria-live="polite"
          onClick={() => onCopy?.(clipText, id, { html: clipHtml })}
          style={{
            padding: '8px 14px',
            borderRadius: RADII.control,
            border: 'none',
            background: active ? COLORS.ink : accent,
            color: COLORS.surface,
            fontSize: '12.5px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: SHADOW.card,
            flexShrink: 0,
          }}
        >
          {active ? <><Check size={13} /> {tr('pieces.ui.copied')}</> : <><Copy size={13} /> {tr('pieces.ui.copyAll')}</>}
        </button>
      </div>

      <div
        ref={boxRef}
        data-testid="ready-box"
        onCopy={onCopyWithinBox(boxRef)}
        style={{
          padding: '12px 16px',
          background: t.inputBg || w(0.02),
          border: `1px solid ${t.border || w(0.06)}`,
          borderRadius: RADII.controlInner,
          color: t.text,
          fontSize: '14px',
          lineHeight: 1.7,
        }}
      >
        <PieceValue piece={piece} t={t} w={w} color={accent} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <PieceCounter count={Array.from(payload).length} limit={piece.limit || null} t={t} />
      </div>
    </div>
  );
};

export default ReadyToPublish;
